import { Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePropertyQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyQuery";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { PropertySection } from "web_myplann/Portfolio/Components/PropertySections/PropertySection";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { getLegacyPropertyTitle } from "web_myplann/Portfolio/Hooks/usePropertyTitle";
import { StyledCenteredStack } from "web_myplann/Shared/Components/StyledCenteredStack";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useNavigateBackOrDefault } from "web_myplann/Shared/Hooks/useNavigateBackOrDefault";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";

export const PropertyDetailPageLegacy: FC = () => {
	const { id } = useParams<{ id: string }>();
	const propertyId = id ? Number(id) : undefined;
	const { t } = useResource();
	const theme = useTheme();
	const navigateBackOrDefault = useNavigateBackOrDefault(DEFAULT_ROUTE);

	const { data: property, isLoading } = usePropertyQuery(propertyId);

	const title = getLegacyPropertyTitle(property, t);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [propertyId]);

	const currencySign = t(Resources.Common.CurrencySign);

	const isNotFound = !isLoading && !property;
	const showEmpty = !isLoading && !isNotFound && hasNoBusinessData(property);

	useEffect(() => {
		if (!isNotFound) return;
		toast.error(t(Resources.Portfolio.Detail.notAvailable), {
			id: "property-detail-not-found",
		});
		navigateBackOrDefault();
	}, [isNotFound, navigateBackOrDefault]);

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={title}
					defaultRoute={Routing.Portfolio.PortfolioList.path()}
					replaceOnBack
				/>
			}
			disableBottomPadding
		>
			{isLoading || isNotFound ? (
				<DetailSectionLoader />
			) : showEmpty ? (
				<StyledCenteredStack spacing={2} px={5}>
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<Stack textAlign="center" gap={0.4}>
						<Typography
							variant="deprecatedH3"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.empty)}
						</Typography>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.emptySubtitle)}
						</Typography>
					</Stack>
				</StyledCenteredStack>
			) : (
				<Stack pt={2}>
					{property && propertyId !== undefined && (
						<PropertySection
							property={property}
							currencySign={currencySign}
							propertyId={propertyId}
						/>
					)}
				</Stack>
			)}
		</MyPlannLayout>
	);
};
